import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useExcIncreaseV1AchQueryServiceAchGet } from '@interxfi/api-client/generated/hooks/exc-increase-v1-ach-query-service-ach-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcIncreaseV1AchState } from '@interxfi/types/generated/api';
import { IncreaseAchEntityMenu } from '../../../../../../../../../../features/vendor/increase/ach/increase-ach-entity-menu';
import { IncreaseAchEventSummary } from '../../../../../../../../../../features/vendor/increase/ach/increase-ach-event';
import { getIncreaseAchStateListItems } from '../../../../../../../../../../features/vendor/increase/ach/increase-ach-list-items';
import { getIncreaseAchDetailPath } from '../../../../../../../../../../features/vendor/increase/ach/increase-ach-routing';
import { IncreaseAchStatusPill } from '../../../../../../../../../../features/vendor/increase/ach/increase-ach-status-pill';
import { IncreaseAchSummaryBar } from '../../../../../../../../../../features/vendor/increase/ach/increase-ach-summary-bar';
import { IncreaseNotificationsOfChangeTable } from '../../../../../../../../../../features/vendor/increase/ach/increase-notifications-of-change-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/increase/ach/_ach/$achId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `increase-ach-${params.achId}`,
        label: 'ACH',
        to: getIncreaseAchDetailPath(params.achId),
        suffix: <CopyButton content={params.achId} label="Copy ACH ID" tooltip={{ copy: params.achId, copied: 'Copied ACH ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Increase ACH'),
  component: IncreaseAchDetail,
});

function getListItems(data: ExcIncreaseV1AchState | undefined, isSimulated: boolean) {
  const items = getIncreaseAchStateListItems(data, { increaseId: !isSimulated });

  return {
    left: [
      {
        id: 'main',
        items: [
          items.data.increaseId,
          items.data.spec.amount,
          items.data.spec.sameDay,
          items.data.spec.isInbound,
          items.data.spec.companyName,
          items.data.spec.companyDescriptiveDate,
          items.data.spec.ownerType,
        ],
      },
      {
        id: 'bankDetails',
        title: 'Bank Details',
        items: [
          items.data.spec.bankDetails.routingNumber,
          items.data.spec.bankDetails.accountNumber,
          { ...items.data.spec.bankDetails.accountHolderName, title: 'Name on Account' },
          items.data.spec.bankDetails.accountType,
        ],
      },
    ] as const satisfies ReadonlyListGroup[],
    right: [
      items.metadata.createdAt,
      items.metadata.updatedAt,
      items.achId,
      { ...items.data.spec.achId, title: 'Payment ACH ID' },
      items.data.spec.sourceId,
      items.data.spec.transferId,
      items.data.spec.programId,
      items.data.spec.accountId,
    ] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load Increase ACH', 'view this Increase ACH');

function IncreaseAchDetail() {
  const { achId } = Route.useParams();
  const request = { achId } as const;
  const { activeRealmIsSimulated } = useActiveRealmAPIContext();
  const { data, error, isPending } = useExcIncreaseV1AchQueryServiceAchGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.ach, activeRealmIsSimulated), [data?.ach, activeRealmIsSimulated]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <IncreaseAchStatusPill isPending={showPending} size="large" status={data?.ach?.status} />
          </HorizontalStack>

          <IncreaseAchEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <IncreaseAchSummaryBar {...request} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <IncreaseAchEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      {data?.ach?.data?.notificationOfChange?.length ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Notifications of Change</Typography.Level3>

            <IncreaseNotificationsOfChangeTable notifications={data.ach.data.notificationOfChange} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
